import { useQuery } from "@tanstack/react-query";
import type { PriceChartDatum } from "../components/price-chart/price-chart";

export interface UsePredictionChartOptions {
  /** Polymarket bucket interval. One of 1m, 1h, 6h, 1d, 1w, max. Default: "1d". */
  interval?: "1m" | "1h" | "6h" | "1d" | "1w" | "max";
  /** Fidelity in minutes. Default: 60. */
  fidelityMinutes?: number;
}

export interface UsePredictionChartResult {
  data: PriceChartDatum[];
  currentPrice: number | undefined;
  isReady: boolean;
  error: Error | null;
}

type PricesHistoryResponse = {
  history?: ReadonlyArray<{ t: number; p: number }>;
};

/**
 * Polymarket CLOB prices-history for a single outcome token.
 * Hits our `/api/polymarket/prices-history` proxy so it works in-browser.
 */
export function usePredictionChart(
  tokenId: string | undefined,
  opts: UsePredictionChartOptions = {},
): UsePredictionChartResult {
  const interval = opts.interval ?? "1d";
  const fidelity = opts.fidelityMinutes ?? 60;

  const q = useQuery({
    queryKey: ["pm", "prices-history", tokenId, interval, fidelity],
    queryFn: async () => {
      if (!tokenId) return { history: [] } satisfies PricesHistoryResponse;
      const u = new URL(
        `${window.location.origin}/api/polymarket/prices-history`,
      );
      u.searchParams.set("market", tokenId);
      u.searchParams.set("interval", interval);
      u.searchParams.set("fidelity", String(fidelity));
      const res = await fetch(u.toString(), {
        headers: { accept: "application/json" },
      });
      if (!res.ok) throw new Error(`prices-history ${res.status}`);
      return (await res.json()) as PricesHistoryResponse;
    },
    enabled: !!tokenId,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const points = q.data?.history ?? [];
  const data: PriceChartDatum[] = points.map((h) => ({
    time: h.t * 1000,
    value: h.p,
  }));
  const currentPrice = data.length > 0 ? data[data.length - 1]!.value : undefined;

  return {
    data,
    currentPrice,
    isReady: q.isSuccess,
    error: (q.error ?? null) as Error | null,
  };
}
